// ── insights.js — 룰 기반 자동 인사이트 엔진 ──────────────────────
// 매장 데이터 종합 분석 → 우선순위별 액션 카드
//
// severity 레벨:
//   'urgent'   — 즉시 조치 (손실, 안전재고 위험 등)
//   'warning'  — 검토 필요 (원가율↑, 매출감소 등)
//   'info'     — 개선 기회 (데이터 보강, 정보 제공)
//
// 우선순위 (sort): urgent > warning > info, 같은 레벨에서는 score 큰 순

import { calcMenuProfitability } from './menuProfitability';
import { calcGoalActuals, GOAL_DEFS, calcAchievement } from './goals';

const fmtWon = n => Math.round(Number(n) || 0).toLocaleString('ko-KR') + '원';

function pushIfApplicable(arr, insight) {
  if (insight) arr.push(insight);
}

// ── 매출 비용 통계 ──
function statsFromRecord(record) {
  const r = record?.revenue || {};
  const c = record?.costs || {};
  const totalRevenue = (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
  const ingredients = Number(c.ingredients) || 0;
  const labor = Number(c.labor) || 0;
  const totalCosts = ingredients + labor + (Number(c.rent) || 0) + (Number(c.utilities) || 0) + (Number(c.other) || 0);
  return {
    totalRevenue, totalCosts, ingredients, labor,
    profit: totalRevenue - totalCosts,
    foodCostRatio: totalRevenue > 0 ? (ingredients / totalRevenue) * 100 : null,
    laborRatio: totalRevenue > 0 ? (labor / totalRevenue) * 100 : null,
    margin: totalRevenue > 0 ? ((totalRevenue - totalCosts) / totalRevenue) * 100 : null,
  };
}

// ── 동 기간 매출 합산 (transactions 기반, 1일 ~ targetDay) ──
function sumIncomeUpToDay(transactions, monthKey, targetDay) {
  if (!monthKey || targetDay < 1) return 0;
  const startStr = `${monthKey}-01`;
  const endStr = `${monthKey}-${String(targetDay).padStart(2, '0')}`;
  let sum = 0;
  for (const t of (transactions || [])) {
    if (t?.type !== 'income') continue;
    const d = String(t?.date || '').slice(0, 10);
    if (d >= startStr && d <= endStr) {
      sum += (Number(t.supply) || 0) + (Number(t.vat) || 0);
    }
  }
  return sum;
}

// ── 메인 함수 ──
export function generateInsights({
  profitLoss = {},
  monthKey,
  prevMonthKey,
  prices = [],
  inventory = {},
  orderHistory = [],
  categories = [],
  inventoryAlertCount = 0,
  goals = null,
  transactions = [],
}) {
  const insights = [];
  const cur = profitLoss?.[monthKey] || {};
  const prev = profitLoss?.[prevMonthKey] || {};
  const curStats = statsFromRecord(cur);
  const prevStats = statsFromRecord(prev);

  // 동 기간 매출 비교용 — 보고 있는 월이 현재 진행 중이면 prorate (1~오늘)
  const now = new Date();
  const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = monthKey === todayMonthKey;
  const targetDay = isCurrentMonth ? now.getDate() : 31;
  const curRevenueProrated = isCurrentMonth
    ? sumIncomeUpToDay(transactions, monthKey, targetDay)
    : curStats.totalRevenue;
  const prevRevenueProrated = isCurrentMonth
    ? sumIncomeUpToDay(transactions, prevMonthKey, targetDay)
    : prevStats.totalRevenue;

  // ── 1. 손실 발생 (urgent) ──
  if (curStats.totalRevenue > 0 && curStats.profit < 0) {
    pushIfApplicable(insights, {
      id: 'loss',
      severity: 'urgent',
      score: 100,
      icon: '🚨',
      title: '이번달 손실 발생',
      desc: `${fmtWon(curStats.profit)} 손실 — 매출 ${fmtWon(curStats.totalRevenue)} vs 비용 ${fmtWon(curStats.totalCosts)}. 즉시 비용 점검 필요`,
      actionLabel: '손익관리 → 비용 점검',
      actionTab: 'profitloss',
    });
  }

  // ── 2. 식재료 원가율 위험 (urgent/warning) ──
  if (curStats.foodCostRatio !== null) {
    if (curStats.foodCostRatio >= 45) {
      pushIfApplicable(insights, {
        id: 'foodcost-urgent',
        severity: 'urgent',
        score: 95,
        icon: '🔴',
        title: '식재료 원가율 위험',
        desc: `${curStats.foodCostRatio.toFixed(1)}% — 외식업 평균 35% 대비 매우 높음. 메뉴 가격 인상 또는 공동구매·대체식재료 검토 필요`,
        actionLabel: '메뉴 가격 도우미',
        actionTab: 'recipes',
      });
    } else if (curStats.foodCostRatio >= 38) {
      pushIfApplicable(insights, {
        id: 'foodcost-high',
        severity: 'warning',
        score: 75,
        icon: '⚠️',
        title: '식재료 원가율 높음',
        desc: `${curStats.foodCostRatio.toFixed(1)}% — 35%↓ 권장. 단가표·공동구매로 절감 검토`,
        actionLabel: '단가표 점검',
        actionTab: 'prices',
      });
    }
  }

  // ── 3. 인건비 비율 위험 ──
  if (curStats.laborRatio !== null) {
    if (curStats.laborRatio >= 35) {
      pushIfApplicable(insights, {
        id: 'labor-urgent',
        severity: 'urgent',
        score: 90,
        icon: '👥',
        title: '인건비 비율 매우 높음',
        desc: `${curStats.laborRatio.toFixed(1)}% — 25%↓ 권장. 스케줄 효율화 또는 매출 증대 필요`,
        actionLabel: '인건비 탭으로',
        actionTab: 'payroll',
      });
    } else if (curStats.laborRatio >= 28) {
      pushIfApplicable(insights, {
        id: 'labor-high',
        severity: 'warning',
        score: 65,
        icon: '👥',
        title: '인건비 부담',
        desc: `${curStats.laborRatio.toFixed(1)}% — 스케줄 조정으로 절감 가능 검토`,
        actionLabel: '스케줄 조정',
        actionTab: 'payroll',
      });
    }
  }

  // ── 4. 매출 급감 — 현재 월이면 동 기간(1~오늘) prorate 비교, 과거 월은 전체 ──
  if (prevRevenueProrated > 0 && curRevenueProrated > 0) {
    const delta = ((curRevenueProrated - prevRevenueProrated) / prevRevenueProrated) * 100;
    const periodLabel = isCurrentMonth ? `${targetDay}일까지 동 기간` : '전월';
    if (delta <= -15) {
      pushIfApplicable(insights, {
        id: 'revenue-drop',
        severity: 'urgent',
        score: 88,
        icon: '📉',
        title: '매출 큰 폭 감소',
        desc: `${periodLabel} 대비 ${delta.toFixed(1)}% 감소 (${fmtWon(prevRevenueProrated)} → ${fmtWon(curRevenueProrated)}). 마케팅·메뉴·계절성 점검 필요`,
        actionLabel: '거래 내역 확인',
        actionTab: 'ledger',
      });
    } else if (delta <= -8) {
      pushIfApplicable(insights, {
        id: 'revenue-soft',
        severity: 'warning',
        score: 60,
        icon: '⚠️',
        title: '매출 감소세',
        desc: `${periodLabel} 대비 ${delta.toFixed(1)}% 감소. 원인 점검 권장`,
        actionLabel: '손익 비교',
        actionTab: 'profitloss',
      });
    } else if (delta >= 15) {
      pushIfApplicable(insights, {
        id: 'revenue-grow',
        severity: 'info',
        score: 40,
        icon: '🎉',
        title: '매출 성장 중',
        desc: `${periodLabel} 대비 +${delta.toFixed(1)}% 성장 (${fmtWon(curRevenueProrated)}). 인기 메뉴 확장·추가 인력 검토 좋음`,
        actionLabel: '메뉴 분석',
        actionTab: 'recipes',
      });
    }
  }

  // ── 5. 발주 알림 ──
  if (inventoryAlertCount >= 5) {
    pushIfApplicable(insights, {
      id: 'inventory-many',
      severity: 'urgent',
      score: 85,
      icon: '📦',
      title: '재고 부족 다수',
      desc: `${inventoryAlertCount}개 식재료가 안전재고 미달 또는 위험. 즉시 발주 권장`,
      actionLabel: '재고관리 → 발주',
      actionTab: 'inventory',
    });
  } else if (inventoryAlertCount >= 1) {
    pushIfApplicable(insights, {
      id: 'inventory-some',
      severity: 'warning',
      score: 55,
      icon: '📦',
      title: '재고 발주 필요',
      desc: `${inventoryAlertCount}개 식재료 발주 권장 — 발주서로 일괄 추가 가능`,
      actionLabel: '재고 점검',
      actionTab: 'inventory',
    });
  }

  // ── 6. 메뉴 마진 분석 ──
  const menus = calcMenuProfitability({ categories, prices });
  const evaluable = menus.filter(m => m.hasPrice && m.hasIngredients && m.marginPct !== null);
  if (evaluable.length >= 3) {
    const lossMenus = evaluable.filter(m => m.marginPct < 0);
    const lowMarginMenus = evaluable.filter(m => m.marginPct >= 0 && m.marginPct < 25);
    if (lossMenus.length > 0) {
      const names = lossMenus.slice(0, 2).map(m => m.name).join(', ');
      pushIfApplicable(insights, {
        id: 'menu-loss',
        severity: 'urgent',
        score: 92,
        icon: '🍽️',
        title: '손해 메뉴 발견',
        desc: `${lossMenus.length}개 메뉴 (${names}${lossMenus.length > 2 ? ' 외' : ''}) — 가격 인상 또는 메뉴 제거 검토`,
        actionLabel: '메뉴 가격 검토',
        actionTab: 'recipes',
      });
    } else if (lowMarginMenus.length >= Math.ceil(evaluable.length * 0.4)) {
      pushIfApplicable(insights, {
        id: 'menu-lowmargin',
        severity: 'warning',
        score: 50,
        icon: '🍽️',
        title: '저마진 메뉴 다수',
        desc: `마진율 25% 미만 ${lowMarginMenus.length}개 — 가격·원가 점검`,
        actionLabel: '레시피 확인',
        actionTab: 'recipes',
      });
    }
  }

  // ── 7. 단가표 빈약 ──
  if (prices.length < 10) {
    pushIfApplicable(insights, {
      id: 'prices-sparse',
      severity: 'info',
      score: 35,
      icon: '🏷️',
      title: '단가표 보강 필요',
      desc: `${prices.length}개 등록 — 10개 이상 입력하면 원가 분석·재고 알림 정확도 ↑`,
      actionLabel: '단가표 추가',
      actionTab: 'prices',
    });
  }

  // ── 8. 거래장부 입력 부족 ──
  // (orderHistory는 발주 내역이라 transactions랑은 다름. 근데 이건 호출측에서 안 넘겨주니 지금은 패스)

  // ── 9. 매출 데이터 미입력 ──
  if (curStats.totalRevenue === 0) {
    pushIfApplicable(insights, {
      id: 'no-revenue',
      severity: 'info',
      score: 30,
      icon: '💰',
      title: '이번달 매출 입력 필요',
      desc: `손익관리 탭에서 매출 입력하면 분석이 시작돼요`,
      actionLabel: '매출 입력',
      actionTab: 'profitloss',
    });
  }

  // ── 10. 목표 달성 / 미달 ──
  if (goals) {
    const actuals = calcGoalActuals(cur);
    GOAL_DEFS.forEach(def => {
      const target = goals[def.id];
      const actual = actuals[def.id];
      const pct = calcAchievement(def, target, actual);
      if (pct !== null && pct >= 100 && curStats.totalRevenue > 0) {
        pushIfApplicable(insights, {
          id: `goal-hit-${def.id}`,
          severity: 'info',
          score: 25,
          icon: '🎯',
          title: `목표 달성: ${def.label}`,
          desc: `${pct.toFixed(0)}% 달성. 다음 달 목표 상향 검토`,
          actionLabel: '목표 수정',
          actionTab: 'home',
        });
      }
    });
  }

  // 정렬 (severity > score)
  const severityRank = { urgent: 3, warning: 2, info: 1 };
  insights.sort((a, b) => {
    const sd = severityRank[b.severity] - severityRank[a.severity];
    if (sd !== 0) return sd;
    return b.score - a.score;
  });

  return insights;
}
